import {Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {CapitalCasePipe} from "../../../pipes/capital-case.pipe";
import {DxButtonComponent, DxDataGridComponent, DxFormComponent, DxTemplateDirective} from "devextreme-angular";
import {
    DxiButtonComponent,
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent, DxoFilterRowComponent, DxoHeaderFilterComponent, DxoLoadPanelComponent, DxoPagerComponent, DxoPagingComponent,
    DxoScrollingComponent, DxoSortingComponent, DxoToolbarComponent
} from "devextreme-angular/ui/nested";
import {UserAvatarEmailComponent} from "../../../components/utils/user-avatar-email/user-avatar-email.component";
import {
    DataSources,
    ChangeRequestGridViewDto,
    ChangeRequestStatus,
    ChangeRequestType, PermissionType, AccessType, AuthService
} from "@services";
import EnumUtils from "../../../shared/enum-utils";
import {NgClass} from "@angular/common";
import {FilterPanelComponent} from "../../../components/library/filter-panel/filter-panel.component";
import {GridPageComponent, FilterConfig} from "../../common/grid-page.component";
import {ChangeRequestPopupComponent} from "./change-request-popup/change-request-popup.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ActivatedRoute, Router} from "@angular/router";

@Component({
    selector: 'app-change-requests',
    imports: [
        CapitalCasePipe,
        DxButtonComponent,
        DxDataGridComponent,
        DxTemplateDirective,
        DxiButtonComponent,
        DxiColumnComponent,
        DxiItemComponent,
        DxoColumnChooserComponent,
        DxoExportComponent,
        DxoFilterRowComponent,
        DxoHeaderFilterComponent,
        DxoLoadPanelComponent,
        DxoPagerComponent,
        DxoPagingComponent,
        DxoScrollingComponent,
        DxoSortingComponent,
        DxoToolbarComponent,
        UserAvatarEmailComponent,
        NgClass,
        DxFormComponent,
        FilterPanelComponent,
        ChangeRequestPopupComponent
    ],
    templateUrl: './change-requests.component.html',
    styleUrl: './change-requests.component.scss',
})
export class ChangeRequestsComponent extends GridPageComponent<ChangeRequestGridViewDto> implements OnInit, OnChanges {
    protected dataSources = inject(DataSources);
    protected auth = inject(AuthService);
    protected route = inject(ActivatedRoute);
    protected router = inject(Router);
    protected destroyRef = inject(DestroyRef);

    @ViewChild(DxDataGridComponent, {static: true}) dataGrid: DxDataGridComponent;

    @Input() employeeId: string | null = null;
    @Input() offset: number = 88;
    @Input() embedded: boolean = false;

    dataSource = this.dataSources.changeRequests(null);
    statusOptions = EnumUtils.getEnumOptions(ChangeRequestStatus);
    typeOptions = EnumUtils.getEnumOptions(ChangeRequestType);
    protected override exportName: string = "Change Requests";

    constructor() {
        super();
        this.init(ChangeRequestGridViewDto);
    }

    ngOnInit() {
        this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async params => {
            if (params['requestId']) {
                this.selectedRow = await this.dataSource.byKey(params['requestId']);
                if (this.selectedRow) {
                    this.isPopupOpened = true;
                } else {
                    await this.router.navigate(['/change-requests']);
                }
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['employeeId']) {
            this.dataSource = this.dataSources.changeRequests(this.employeeId);
        }
    }

    protected override filterConfig: FilterConfig[] = [
        { field: 'employee.name', op: 'contains', customFilter: this.nameOrEmailFilter },
        { field: 'submittedDate', op: ">=" },
        { field: 'typeOfChange' },
        { field: 'status' },
        { field: 'number', op: 'contains' }
    ];

    isEditVisible = (e: { row: { data: ChangeRequestGridViewDto } }) => {
        return e.row.data.status === ChangeRequestStatus.Pending;
    }

    isViewVisible = (e: { row: { data: ChangeRequestGridViewDto } }) => {
        return e.row.data.status !== ChangeRequestStatus.Pending;
    }

    protected getBadgeClass(status: ChangeRequestStatus): string {
        switch (status) {
            case ChangeRequestStatus.Pending:
                return 'bg-secondary-subtle text-secondary';
            case ChangeRequestStatus.Approved:
                return 'bg-info-subtle text-info';
            case ChangeRequestStatus.Declined:
                return 'bg-danger-subtle text-danger';
            case ChangeRequestStatus.Withdrawn:
                return 'bg-warning-subtle text-warning';
            case ChangeRequestStatus.Completed:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-info-subtle text-info';
        }
    }

    async onStatusChange(status: ChangeRequestStatus) {
        await this.dataGrid.instance.refresh();
    }

    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;
}
